import { EV } from '../core/eventbus.js';
import { getProp, getSeat, getSeats } from '../data/items.js';

/**
 * 一桌多席（游戏桌四条凳）挑位子：
 * 离自己最近、又没人占的那条。都坐满了就随便挑第一条，不至于站那儿干瞪眼。
 */
function pickSeat(scene, prop, x, y) {
  const seats = getSeats(prop.type);
  if (!seats.length) return null;
  if (seats.length === 1) return seats[0];
  const ts = scene.tilemap ? scene.tilemap.tileSize : 40;
  const taken = new Set();
  for (const e of scene.query('pose')) {
    const po = e.pose;
    if (po && po.on === prop.id && po.state !== 'stand') taken.add(po.seatIndex ?? -1);
  }
  let best = null, bestD = Infinity;
  for (const s of seats) {
    if (taken.has(s.index)) continue;
    const d = Math.hypot((prop.gx + s.ox) * ts - x, (prop.gy + s.oy) * ts - y);
    if (d < bestD) { bestD = d; best = s; }
  }
  return best || seats[0];
}

/**
 * 交互系统：找出玩家附近可交互的器物，设置焦点；按下交互键时广播事件。
 * 具体行为由 actions 系统订阅实现，这里只负责"指认"与"触发"。
 */
export const interactionSystem = {
  name: 'interaction',
  update(dt, ctx) {
    const { scene, input, events, engine } = ctx;
    const p = scene.first('transform', 'player');
    if (!p || !engine.renderer) return;

    // 布置模式下只管摆家具，不弹交互提示
    if (engine.edit && engine.edit.active) {
      if (engine.renderer) engine.renderer.view.focus = null;
      return;
    }

    const ts = scene.tilemap ? scene.tilemap.tileSize : 40;
    const pose = p.pose || { state: 'stand', on: null };
    let best = null;
    let bestD = 62;

    for (const prop of scene.props) {
      const def = getProp(prop.type);
      // 能坐能躺的器物也是交互目标；多条凳子就挑最顺手的那条
      const seat = getSeat(prop.type)
        ? pickSeat(scene, prop, p.transform.x, p.transform.y) : null;
      if (!def.interact && !seat) continue;
      const cx = (prop.gx + prop.w / 2) * ts;
      const cy = (prop.gy + prop.h / 2) * ts;
      const d = Math.hypot(cx - p.transform.x, cy - p.transform.y);
      // 站的朝向越正对，优先级越高
      const bias = p.anim.facing === 'up' ? -6 : 0;
      if (d + bias < bestD) {
        bestD = d + bias;
        best = { prop, def, seat, cx, cy };
      }
    }

    const view = engine.renderer.view;
    if (best) {
      const seatedHere = pose.on != null && pose.on === best.prop.id;
      let hint;
      if (!seatedHere && pose.state === 'stand' && best.seat) {
        hint = best.seat.pose === 'lie' ? 'E 躺下' : 'E 坐下';
      } else if (best.def.interact) {
        hint = best.def.interact.hint || 'E';
      } else {
        hint = 'E 起身';
      }
      view.focus = {
        prop: best.prop,
        label: best.def.interact ? (best.def.interact.label || best.def.name) : best.def.name,
        hint,
        cx: best.cx,
        cy: best.cy - (best.prop.h * ts) / 2,
      };
      if (scene._lastFocusId !== best.prop.id) {
        scene._lastFocusId = best.prop.id;
        events.emit(EV.INTERACT_NEAR, { prop: best.prop, def: best.def });
      }
      // 交互用 E 或回车：空格已经让给跳跃了，免得想跳却坐到凳子上
      if (input.justPressed('e') || input.justPressed('Enter')) {
        // 站着 + 能坐 -> 先坐上去；已经坐/躺在这件上 -> 才是打坐、歇息、存物
        if (!seatedHere && pose.state === 'stand' && best.seat) {
          events.emit(EV.POSE_SIT, { prop: best.prop, def: best.def, seat: best.seat });
        } else if (best.def.interact) {
          events.emit(EV.INTERACT_DO, { prop: best.prop, def: best.def, action: best.def.interact.action });
        } else {
          events.emit(EV.POSE_UP, {});   // 地毯这类没有别的功能，按 E 就是起身
        }
      }
    } else {
      if (scene._lastFocusId !== null) {
        scene._lastFocusId = null;
        events.emit(EV.INTERACT_LEAVE, {});
      }
      view.focus = null;
    }
  },
};
